import { API_URL } from '../config';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`
});

// Budget
export const getBudget = async (projectId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch budget');
  return response.json();
};

export const getBudgetSummary = async (projectId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/summary`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch budget summary');
  return response.json();
};

export const createBudget = async (projectId, budgetData) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(budgetData)
  });
  if (!response.ok) throw new Error('Failed to create budget');
  return response.json();
};

export const updateBudget = async (projectId, budgetId, budgetData) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/${budgetId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(budgetData)
  });
  if (!response.ok) throw new Error('Failed to update budget');
  return response.json();
};

export const deleteBudget = async (projectId, budgetId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/${budgetId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to delete budget');
  return response.json();
};

// Line Items
export const addLineItem = async (projectId, budgetId, lineItem) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/${budgetId}/line-items`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(lineItem)
  });
  if (!response.ok) throw new Error('Failed to add line item');
  return response.json();
};

export const updateLineItem = async (projectId, budgetId, lineItemId, lineItem) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/${budgetId}/line-items/${lineItemId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(lineItem)
  });
  if (!response.ok) throw new Error('Failed to update line item');
  return response.json();
};

export const deleteLineItem = async (projectId, budgetId, lineItemId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/${budgetId}/line-items/${lineItemId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to delete line item');
  return response.json();
};

// Expenses
export const getExpenses = async (projectId, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/expenses?${queryString}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch expenses');
  return response.json();
};

export const createExpense = async (projectId, expenseData) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/expenses`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(expenseData)
  });
  if (!response.ok) throw new Error('Failed to create expense');
  return response.json();
};

export const updateExpense = async (projectId, expenseId, expenseData) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/expenses/${expenseId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(expenseData)
  });
  if (!response.ok) throw new Error('Failed to update expense');
  return response.json();
};

export const approveExpense = async (projectId, expenseId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/expenses/${expenseId}/approve`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to approve expense');
  return response.json();
};

export const rejectExpense = async (projectId, expenseId, reason) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/expenses/${expenseId}/reject`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason })
  });
  if (!response.ok) throw new Error('Failed to reject expense');
  return response.json();
};

export const deleteExpense = async (projectId, expenseId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/expenses/${expenseId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to delete expense');
  return response.json();
};

// Purchase Orders
export const getPurchaseOrders = async (projectId, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/purchase-orders?${queryString}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch purchase orders');
  return response.json();
};

export const getPurchaseOrder = async (projectId, poId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/purchase-orders/${poId}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch purchase order');
  return response.json();
};

export const createPurchaseOrder = async (projectId, poData) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/purchase-orders`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(poData)
  });
  if (!response.ok) throw new Error('Failed to create purchase order');
  return response.json();
};

export const updatePurchaseOrder = async (projectId, poId, poData) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/purchase-orders/${poId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(poData)
  });
  if (!response.ok) throw new Error('Failed to update purchase order');
  return response.json();
};

export const approvePurchaseOrder = async (projectId, poId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/purchase-orders/${poId}/approve`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to approve purchase order');
  return response.json();
};

export const rejectPurchaseOrder = async (projectId, poId, reason) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/purchase-orders/${poId}/reject`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason })
  });
  if (!response.ok) throw new Error('Failed to reject purchase order');
  return response.json();
};

export const deletePurchaseOrder = async (projectId, poId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/purchase-orders/${poId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to delete purchase order');
  return response.json();
};

// Categories
export const getCategories = async () => {
  const response = await fetch(`${API_URL}/api/v1/budget/categories`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch categories');
  return response.json();
};

// PDF Exports
export const exportTopSheetPDF = async (projectId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/export/topsheet-pdf`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to export PDF');
  return response.blob();
};

export const exportDetailPDF = async (projectId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/export/detail-pdf`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to export PDF');
  return response.blob();
};

export const exportCostReportPDF = async (projectId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/export/cost-report-pdf`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to export PDF');
  return response.blob();
};

// CSV Export/Import
export const exportCSV = async (projectId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/export/csv`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to export CSV');
  return response.blob();
};

export const importCSV = async (projectId, budgetId, csvData) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/${budgetId}/import/csv`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ csvData })
  });
  if (!response.ok) throw new Error('Failed to import CSV');
  return response.json();
};

// Versions
export const getVersions = async (projectId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/versions`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch versions');
  return response.json();
};

export const createVersion = async (projectId, budgetId, name) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/${budgetId}/versions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name })
  });
  if (!response.ok) throw new Error('Failed to create version');
  return response.json();
};

export const compareVersions = async (projectId, version1, version2) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/versions/compare?version1=${version1}&version2=${version2}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to compare versions');
  return response.json();
};

// Templates
export const getTemplates = async () => {
  const response = await fetch(`${API_URL}/api/v1/budget/templates`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch templates');
  return response.json();
};

export const applyTemplate = async (projectId, templateId) => {
  const response = await fetch(`${API_URL}/api/v1/budget/${projectId}/apply-template`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ templateId })
  });
  if (!response.ok) throw new Error('Failed to apply template');
  return response.json();
};

// Utility functions
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(amount || 0);
};

export const calculateVariance = (budgeted, actual) => {
  const variance = budgeted - actual;
  const percentage = budgeted > 0 ? (variance / budgeted) * 100 : 0;
  return { variance, percentage };
};

// Download helper
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};
